"use client";

import { useState } from "react";
import type { VideoContext, VideoEmbed, VideoResolver } from "./types";

export function VideoPlayer({
  url,
  title,
  resolve,
  context,
}: {
  url: string;
  title: string;
  resolve: VideoResolver;
  context?: VideoContext;
}) {
  const source = resolve(url, context);
  if (!source) return null;
  return (
    <div className="video-player">
      {source.embed ? (
        <EmbeddedVideo
          key={source.embed.src}
          embed={source.embed}
          title={title}
        />
      ) : (
        <p className="video-player-note">この動画はリンク先でご覧ください。</p>
      )}
      <a
        className="video-player-link"
        href={source.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        動画を別タブで開く <span aria-hidden="true">↗</span>
      </a>
      {source.embed && (
        <p className="video-player-note">
          再生できない場合は、リンク先でご覧ください。
        </p>
      )}
    </div>
  );
}

function EmbeddedVideo({ embed, title }: { embed: VideoEmbed; title: string }) {
  const [failed, setFailed] = useState(false);
  if (failed)
    return (
      <p className="video-player-note" role="status">
        動画を読み込めませんでした。
      </p>
    );
  return (
    <div
      className={`video-player-frame${embed.portrait ? " is-portrait" : ""}`}
    >
      {embed.kind === "video" ? (
        // External videos may not provide captions; do not fabricate a track.
        // biome-ignore lint/a11y/useMediaCaption: user-provided external video, captions are controlled by its publisher.
        <video
          src={embed.src}
          controls
          playsInline
          preload="none"
          aria-label={title}
          onError={() => setFailed(true)}
        />
      ) : (
        <iframe
          src={embed.src}
          title={`${title}（${embed.provider}）`}
          loading="lazy"
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}
